/** Master portal: ClearIFC's own view across every Enterprise org --
 * create orgs, set pricing/terms, stand up an org's first admin, and see
 * every org's pending "Purchase more seats" requests in one place.
 *
 * Gated by CLEARIFC_ADMIN_KEY, not a customer login -- see App.tsx's
 * /platform-admin route and MASTER_GUIDE.md's routine tasks for the
 * curl-equivalent of everything here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { AccountProfile, OrgSummary, PendingPurchaseRequest } from '../lib/types';
import { Badge, Callout, Logo, Spinner, formatDateTime } from './primitives';

function formatCents(cents: number | null): string {
  if (cents === null) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const TIERS = ['free', 'professional', 'team', 'enterprise'] as const;

export function PlatformAdminView() {
  const [key, setKey] = useState(api.getPlatformAdminKey());
  const [unlocked, setUnlocked] = useState(!!api.getPlatformAdminKey());
  const [orgs, setOrgs] = useState<OrgSummary[] | null>(null);
  const [pending, setPending] = useState<PendingPurchaseRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pricingOrgId, setPricingOrgId] = useState<string | null>(null);
  const [adminOrgId, setAdminOrgId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const [orgPage, pendingPage] = await Promise.all([
        api.listAllOrgs(),
        api.listPendingPurchaseRequests(),
      ]);
      setOrgs(orgPage.orgs);
      setPending(pendingPage.requests);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 401 || caught.status === 403 || caught.status === 404)) {
        setUnlocked(false);
        api.setPlatformAdminKey('');
        setError('That admin key was rejected.');
      } else {
        setError(caught instanceof ApiError ? caught.message : 'Could not load the master portal.');
      }
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) void load();
  }, [unlocked, load]);

  async function fulfil(requestId: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.fulfilPurchaseRequestAsPlatformAdmin(requestId);
      setNotice(`Fulfilled -- ${result.org.name}'s seat limit is now ${result.org.seat_limit}.`);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not fulfil that request.');
    } finally {
      setBusy(false);
    }
  }

  if (!unlocked) {
    return (
      <div className="app">
        <main className="main">
          <div className="container" style={{ maxWidth: '420px', margin: '4rem auto' }}>
            <div className="card">
              <div className="card-head">
                <div className="logo">
                  <Logo />
                  <div>Master portal</div>
                </div>
              </div>
              <div className="card-body stack">
                <p className="hint">Enter the platform admin key (CLEARIFC_ADMIN_KEY).</p>
                <input
                  type="password"
                  value={key}
                  onChange={(event) => setKey(event.target.value)}
                  placeholder="Admin key"
                  aria-label="Admin key"
                  autoFocus
                />
                {error && <Callout variant="error">{error}</Callout>}
                <button
                  type="button"
                  className="btn btn-accent"
                  onClick={() => {
                    api.setPlatformAdminKey(key);
                    setError(null);
                    setUnlocked(true);
                  }}
                  disabled={!key.trim()}
                >
                  Unlock
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <main className="main">
        <div className="container stack" style={{ paddingTop: '1.5rem' }}>
          <div className="page-head">
            <h1>Master portal</h1>
            <p>Every Enterprise org, in one place. See MASTER_GUIDE.md for the equivalent curl commands.</p>
          </div>

          {error && <Callout variant="error">{error}</Callout>}
          {notice && <Callout variant="info">{notice}</Callout>}

          <div className="card">
            <div className="card-head">
              <h2>Pending purchase requests {pending ? `(${pending.length})` : ''}</h2>
              <div className="masthead-spacer" />
              <button type="button" className="btn btn-sm" onClick={() => void load()} disabled={busy}>
                Refresh
              </button>
            </div>
            {busy && !pending ? (
              <div className="card-body tight row">
                <Spinner /> <span className="hint-strong">Loading…</span>
              </div>
            ) : pending && pending.length === 0 ? (
              <div className="card-body tight">
                <p className="hint">No pending requests.</p>
              </div>
            ) : (
              pending && (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Org</th>
                        <th>Seats</th>
                        <th>Payment</th>
                        <th>Start date</th>
                        <th>Estimate</th>
                        <th>Requested</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map((r) => (
                        <tr key={r.id}>
                          <td>{r.org_name}</td>
                          <td>{r.seats_requested}</td>
                          <td>{r.payment_choice}</td>
                          <td className="cell-mono">{r.start_date}</td>
                          <td>{formatCents(r.prorated_amount_cents)}</td>
                          <td className="cell-mono">{formatDateTime(r.created_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-accent"
                              disabled={busy}
                              onClick={() => void fulfil(r.id)}
                            >
                              Mark fulfilled
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Orgs {orgs ? `(${orgs.length})` : ''}</h2>
              <div className="masthead-spacer" />
              <button type="button" className="btn btn-sm btn-accent" onClick={() => setCreateOpen(true)}>
                Create org
              </button>
            </div>
            {orgs && orgs.length === 0 ? (
              <div className="card-body tight">
                <p className="hint">No orgs yet.</p>
              </div>
            ) : (
              orgs && (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Seats</th>
                        <th>Rate</th>
                        <th>Term</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {orgs.map((org) => (
                        <tr key={org.id}>
                          <td>{org.name}</td>
                          <td>
                            {org.seats_used} of {org.seat_limit}
                          </td>
                          <td>{formatCents(org.price_per_seat_cents)}/seat/yr</td>
                          <td className="cell-mono">
                            {org.term_start_date ?? '—'} → {org.term_end_date ?? '—'}
                          </td>
                          <td>
                            <div className="row">
                              <button type="button" className="btn btn-sm" onClick={() => setPricingOrgId(org.id)}>
                                Set pricing
                              </button>
                              <button type="button" className="btn btn-sm" onClick={() => setAdminOrgId(org.id)}>
                                Add admin
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>

          <AccountsPanel orgs={orgs ?? []} />

          <IdsxDecryptorPanel />
        </div>
      </main>

      {createOpen && (
        <CreateOrgDialog
          onClose={() => setCreateOpen(false)}
          onCreated={async (message) => {
            setCreateOpen(false);
            setNotice(message);
            await load();
          }}
        />
      )}
      {pricingOrgId && (
        <SetPricingDialog
          orgId={pricingOrgId}
          org={orgs?.find((o) => o.id === pricingOrgId) ?? null}
          onClose={() => setPricingOrgId(null)}
          onSaved={async (message) => {
            setPricingOrgId(null);
            setNotice(message);
            await load();
          }}
        />
      )}
      {adminOrgId && (
        <AddOrgAdminDialog
          orgId={adminOrgId}
          onClose={() => setAdminOrgId(null)}
          onAdded={async (message) => {
            setAdminOrgId(null);
            setNotice(message);
            await load();
          }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------- accounts
/** Every account regardless of tier -- free, professional, team, and
 * enterprise (including org-pooled seats), with a tier filter, an org
 * filter (a specific org, or "no org" for accounts paying for
 * themselves), and a name/email search that applies across whichever of
 * those is selected, not instead of it. */
function AccountsPanel({ orgs }: { orgs: OrgSummary[] }) {
  const [accounts, setAccounts] = useState<AccountProfile[] | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<'all' | (typeof TIERS)[number]>('all');
  const [orgFilter, setOrgFilter] = useState<'all' | 'none' | string>('all');
  const [search, setSearch] = useState('');
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<{ accountId: string; key: string } | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const page = await api.listAllAccounts();
      setAccounts(page.accounts);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load accounts.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!accounts) return [];
    const needle = search.trim().toLowerCase();
    return accounts.filter((account) => {
      if (tierFilter !== 'all' && account.tier !== tierFilter) return false;
      if (orgFilter === 'none' && account.org_id) return false;
      if (orgFilter !== 'all' && orgFilter !== 'none' && account.org_id !== orgFilter) return false;
      if (!needle) return true;
      return account.name.toLowerCase().includes(needle) || account.email.toLowerCase().includes(needle);
    });
  }, [accounts, tierFilter, orgFilter, search]);

  function orgName(orgId: string | null): string {
    if (!orgId) return '—';
    return orgs.find((o) => o.id === orgId)?.name ?? orgId;
  }

  async function changeTier(account: AccountProfile, tier: string) {
    setRowBusy(account.id);
    setError(null);
    setNotice(null);
    try {
      await api.updateAccountAsPlatformAdmin(account.id, { tier });
      setNotice(`${account.email || account.name} is now ${tier}.`);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change that tier.');
    } finally {
      setRowBusy(null);
    }
  }

  async function toggleDisabled(account: AccountProfile) {
    setRowBusy(account.id);
    setError(null);
    setNotice(null);
    try {
      await api.updateAccountAsPlatformAdmin(account.id, { disabled: !account.disabled });
      setNotice(`${account.email || account.name} ${account.disabled ? 're-enabled' : 'disabled'}.`);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not update that account.');
    } finally {
      setRowBusy(null);
    }
  }

  async function rotateKey(account: AccountProfile) {
    setRowBusy(account.id);
    setError(null);
    setNotice(null);
    try {
      const result = await api.rotateAccountKeyAsPlatformAdmin(account.id);
      setRevealedKey({ accountId: account.id, key: result.api_key });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not rotate that key.');
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>All accounts {accounts ? `(${filtered.length} of ${accounts.length})` : ''}</h2>
        <div className="masthead-spacer" />
        <button type="button" className="btn btn-sm" onClick={() => void load()} disabled={busy}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="card-body tight">
          <Callout variant="error">{error}</Callout>
        </div>
      )}
      {notice && (
        <div className="card-body tight">
          <Callout variant="info">{notice}</Callout>
        </div>
      )}
      {revealedKey && (
        <div className="card-body tight">
          <Callout variant="upsell">
            <strong>New key (shown once):</strong>{' '}
            <span className="cell-mono">{revealedKey.key}</span>
            <button
              type="button"
              className="btn btn-sm"
              style={{ marginLeft: '0.6rem' }}
              onClick={() => setRevealedKey(null)}
            >
              Dismiss
            </button>
          </Callout>
        </div>
      )}

      <div className="card-body tight row">
        <input
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ flex: 1, minWidth: '12rem' }}
          aria-label="Search accounts"
        />
        <select
          value={tierFilter}
          onChange={(event) => setTierFilter(event.target.value as typeof tierFilter)}
          aria-label="Filter by tier"
        >
          <option value="all">All tiers</option>
          {TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {tier}
            </option>
          ))}
        </select>
        <select
          value={orgFilter}
          onChange={(event) => setOrgFilter(event.target.value)}
          aria-label="Filter by org"
        >
          <option value="all">All (any/no org)</option>
          <option value="none">No org (individual)</option>
          {orgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
      </div>

      {busy && !accounts ? (
        <div className="card-body tight row">
          <Spinner /> <span className="hint-strong">Loading…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-body tight">
          <p className="hint">No accounts match this filter.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Tier</th>
                <th>Org</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((account) => (
                <tr key={account.id}>
                  <td>{account.name}</td>
                  <td className="cell-mono">{account.email || '—'}</td>
                  <td>
                    <select
                      value={account.tier}
                      disabled={rowBusy === account.id}
                      onChange={(event) => void changeTier(account, event.target.value)}
                      aria-label={`Tier for ${account.name}`}
                    >
                      {TIERS.map((tier) => (
                        <option key={tier} value={tier}>
                          {tier}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {orgName(account.org_id)}
                    {account.org_role && <span className="hint"> ({account.org_role})</span>}
                  </td>
                  <td>
                    <Badge variant={account.disabled ? 'fail' : 'pass'}>
                      {account.disabled ? 'Disabled' : 'Active'}
                    </Badge>
                  </td>
                  <td className="cell-mono">{formatDateTime(account.created_at)}</td>
                  <td>
                    <div className="row">
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={rowBusy === account.id}
                        onClick={() => void toggleDisabled(account)}
                      >
                        {account.disabled ? 'Enable' : 'Disable'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={rowBusy === account.id}
                        onClick={() => void rotateKey(account)}
                      >
                        Rotate key
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------- idsx decryptor
/** Reads a locked .idsx file's actual contents -- an audit/dispute tool,
 * not a normal part of anyone's workflow (validation never needs this;
 * see routes_org.py's decrypt_idsx for why it's platform-admin only). */
function IdsxDecryptorPanel() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<api.DecryptedIdsx | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decrypt() {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.decryptIdsxAsPlatformAdmin(file));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not decrypt that file.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Decrypt an .idsx file</h2>
      </div>
      <div className="card-body stack">
        <p className="hint">
          Reads a locked IDS file's actual rules -- for an audit or a dispute over what was
          locked. Not part of normal validation, which never needs this.
        </p>
        <div className="row">
          <input
            type="file"
            accept=".idsx"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setResult(null);
              setError(null);
            }}
            aria-label="Select an .idsx file"
          />
          <button type="button" className="btn btn-sm btn-accent" onClick={() => void decrypt()} disabled={!file || busy}>
            {busy ? 'Decrypting…' : 'Decrypt'}
          </button>
        </div>

        {error && <Callout variant="error">{error}</Callout>}

        {result && (
          <>
            <div className="meta-list">
              <div className="meta-row">
                <span className="meta-key">Rule set</span>
                <span className="meta-value">{result.name}</span>
              </div>
              <div className="meta-row">
                <span className="meta-key">Locked by</span>
                <span className="meta-value">{result.account_name}</span>
              </div>
              <div className="meta-row">
                <span className="meta-key">Locked at</span>
                <span className="meta-value">{formatDateTime(result.locked_at)}</span>
              </div>
              {(result.requested_by_name || result.requested_by_email) && (
                <div className="meta-row">
                  <span className="meta-key">Requested by</span>
                  <span className="meta-value">
                    {[result.requested_by_name, result.requested_by_email]
                      .filter(Boolean)
                      .join(' — ')}
                  </span>
                </div>
              )}
              {result.project_name && (
                <div className="meta-row">
                  <span className="meta-key">Project</span>
                  <span className="meta-value">{result.project_name}</span>
                </div>
              )}
              {result.comments && (
                <div className="meta-row">
                  <span className="meta-key">Comments</span>
                  <span className="meta-value">{result.comments}</span>
                </div>
              )}
              <div className="meta-row">
                <span className="meta-key">SHA-256</span>
                <span className="meta-value cell-mono">{result.ids_sha256}</span>
              </div>
            </div>
            <pre className="xml-preview">{result.xml}</pre>
          </>
        )}
      </div>
    </div>
  );
}

function CreateOrgDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (message: string) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [seatLimit, setSeatLimit] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const org = await api.createOrgAsPlatformAdmin({ name, seat_limit: seatLimit });
      await onCreated(`Created ${org.name} (${seatLimit} seats). Set pricing and add an admin next.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create that org.');
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Create org">
      <div className="card">
        <div className="card-head">
          <h2>Create org</h2>
        </div>
        <div className="card-body stack">
          <label className="field">
            <span>Name</span>
            <input type="text" value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          </label>
          <label className="field">
            <span>Seat limit</span>
            <input
              type="number"
              min={1}
              value={seatLimit}
              onChange={(event) => setSeatLimit(Math.max(1, Number(event.target.value) || 1))}
            />
          </label>
          {error && <Callout variant="error">{error}</Callout>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-accent" onClick={() => void submit()} disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function SetPricingDialog({
  orgId,
  org,
  onClose,
  onSaved,
}: {
  orgId: string;
  org: OrgSummary | null;
  onClose: () => void;
  onSaved: (message: string) => void | Promise<void>;
}) {
  const [priceDollars, setPriceDollars] = useState(
    org?.price_per_seat_cents !== null && org?.price_per_seat_cents !== undefined
      ? (org.price_per_seat_cents / 100).toString()
      : '',
  );
  const [termStart, setTermStart] = useState(org?.term_start_date ?? todayIso());
  const [termEnd, setTermEnd] = useState(
    org?.term_end_date ?? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const cents = Math.round(Number(priceDollars) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setError('Enter a valid price per seat.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.setOrgPricingAsPlatformAdmin(orgId, {
        price_per_seat_cents: cents,
        term_start_date: termStart,
        term_end_date: termEnd,
      });
      await onSaved(`Pricing updated: $${(cents / 100).toFixed(2)}/seat/year, ${termStart} → ${termEnd}.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save pricing.');
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Set pricing">
      <div className="card">
        <div className="card-head">
          <h2>Set pricing{org ? ` — ${org.name}` : ''}</h2>
        </div>
        <div className="card-body stack">
          <label className="field">
            <span>Price per seat, per year (AUD)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={priceDollars}
              onChange={(event) => setPriceDollars(event.target.value)}
              autoFocus
            />
          </label>
          <label className="field">
            <span>Term start</span>
            <input type="date" value={termStart} onChange={(event) => setTermStart(event.target.value)} />
          </label>
          <label className="field">
            <span>Term end</span>
            <input type="date" value={termEnd} onChange={(event) => setTermEnd(event.target.value)} />
          </label>
          {error && <Callout variant="error">{error}</Callout>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-accent" onClick={() => void submit()} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddOrgAdminDialog({
  orgId,
  onClose,
  onAdded,
}: {
  orgId: string;
  onClose: () => void;
  onAdded: (message: string) => void | Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const account = await api.addOrgAdminAsPlatformAdmin(orgId, { email, name: name || undefined });
      await onAdded(`${account.email} is now an admin of this org.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add that admin.');
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Add org admin">
      <div className="card">
        <div className="card-head">
          <h2>Add org admin</h2>
        </div>
        <div className="card-body stack">
          <label className="field">
            <span>Email address</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
            />
          </label>
          <label className="field">
            <span>Name (optional)</span>
            <input type="text" value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          {error && <Callout variant="error">{error}</Callout>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => void submit()}
              disabled={busy || !email.includes('@')}
            >
              {busy ? 'Adding…' : 'Add admin'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
